import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { IShopContext } from "../stores";
import { useStores } from "../stores/root-store";
import { observer } from "mobx-react";
import { HubConnection } from "@microsoft/signalr";
import useSound from "use-sound";
import dingSound from "../resources/sounds/ding.mp3";
import { creatHubConnection, timeToDate } from "../utils/functions";
import { ApiConfig } from "../services/api-config";
import { IShopSettings } from "../stores/shopSettings";
import moment from "moment";

export const ShopContext = createContext({} as IShopContext);

const defaultHourDelivery = 60,
  defaultHourCollection = 20;

interface IShopContextProvider {
  children: ReactElement;
}

const ShopContextProvider = observer(({ children }: IShopContextProvider) => {
  const { shopSettingsStore } = useStores();
  const { shopSettings, getShopSettings } = shopSettingsStore;

  const [shopId, setShopId] = useState(1);
  const [isShopClosed, setIsShopClosed] = useState<boolean>(false);
  const [shopStartHour, setShopStartHour] = useState<string>("");
  const [shopEndHour, setShopEndHour] = useState<string>("");
  const [shopOpenHoursInterval, setShopOpenHoursInterval] = useState<number>(0);
  const [allowFutureDateOrders, setAllowFutureDateOrders] =
    useState<boolean>(false);
  const [noOfFutureDaysAllowed, setNoOfFutureDaysAllowed] = useState(7);
  const [minimumOrderAmount, setMinimumOrderAmount] = useState<number>(0);
  const [minimumCollectionOrderAmount, setMinimumCollectionOrderAmount] =
    useState<number>(0);
  const [defaultDeliveryCost, setDefaultDeliveryCost] = useState<number>(0);
  const [weeklyShopClosureDays, setWeeklyShopClosureDays] =
    useState<string>("");
  const [estimatedTimeForDelivery, setEstimatedTimeForDelivery] =
    useState<number>(defaultHourDelivery);
  const [estimatedTimeForCollection, setEstimatedTimeForCollection] =
    useState<number>(defaultHourCollection);
  const [notifyWhatsappNumber, setNotifyWhatsappNumber] = useState("");
  const [adminWhatsappNumber, setAdminWhatsappNumber] = useState("");
  const [orderDeliveryRadius, setOrderDeliveryRadius] = useState(5);
  const [shopPostcode, setShopPostcode] = useState("");
  const [cashPaymentCollection, setCashPaymentCollection] = useState(false);
  const [cashPaymentDelivery, setCashPaymentDelivery] = useState(false);
  const [shopTableCount, setshopTableCount] = useState<number>(0);

  const [hubConnection, setHubConnection] = useState<HubConnection>();
  const [playSound] = useSound(dingSound);

  const settingsHubCallback = (settings: IShopSettings) => {
    if (settings) {
      playSound();
      setShopSettings(settings);
    }
  };

  useEffect(() => {
    const connection = creatHubConnection(
      ApiConfig.REST_URIS.shopSettings.shopSettingsHub,
      "ReceiveShopSettings",
      settingsHubCallback
    );
    setHubConnection(connection);

    return () => {
      hubConnection?.stop();
    };
  }, []);

  useEffect(() => {
    if (!shopSettings.shopId) getShopSettings();
    else setShopSettings(shopSettings);
  }, [shopSettings]);

  const checkShopClosedHours = (startHour: string, endHour: string) => {
    const now = moment().toDate();
    const start = timeToDate(startHour);
    const end = timeToDate(endHour);
    if (!start || !end) return true;
    //check if open hours
    const isOpenHours = now >= start && now < end;
    return !isOpenHours;
  };

  const setShopSettings = (shopSettings: IShopSettings) => {
    setShopId(shopSettings.shopId);
    const today = new Date().getDay();
    //check if open hours
    const isClosedHours = checkShopClosedHours(
      shopSettings.shopStartHour,
      shopSettings.shopEndHour
    );
    //check if weekly closure day
    const isWeeklyClosureDay = shopSettings.weeklyShopClosureDays?.includes(
      today.toString()
    );
    setIsShopClosed(
      shopSettings.isShopClosed || isClosedHours || isWeeklyClosureDay
    );
    setShopStartHour(shopSettings.shopStartHour);
    setShopEndHour(shopSettings.shopEndHour);
    setShopOpenHoursInterval(shopSettings.shopOpenHoursInterval);
    setAllowFutureDateOrders(shopSettings.allowFutureDateOrders);
    setNoOfFutureDaysAllowed(shopSettings.noOfFutureDaysAllowed);
    setMinimumOrderAmount(shopSettings.minimumOrderAmount);
    setMinimumCollectionOrderAmount(shopSettings.minimumCollectionOrderAmount);
    setDefaultDeliveryCost(shopSettings.defaultDeliveryCost);
    setWeeklyShopClosureDays(shopSettings.weeklyShopClosureDays);
    setEstimatedTimeForDelivery(
      shopSettings.estimatedTimeForDelivery ?? defaultHourDelivery
    );
    setEstimatedTimeForCollection(
      shopSettings.estimatedTimeForCollection ?? defaultHourCollection
    );
    setNotifyWhatsappNumber(shopSettings.notifyWhatsappNumber);
    setAdminWhatsappNumber(shopSettings.adminWhatsappNumber);
    setOrderDeliveryRadius(shopSettings.orderDeliveryRadius);
    setShopPostcode(shopSettings.shopPostcode);
    setCashPaymentCollection(shopSettings.cashPaymentCollection);
    setCashPaymentDelivery(shopSettings.cashPaymentDelivery);
    setshopTableCount(shopSettings.shopTableCount);

    const interval = setInterval(() => {
      setIsShopClosed(
        checkShopClosedHours(
          shopSettings.shopStartHour,
          shopSettings.shopEndHour
        )
      );
    }, 59000);

    return () => clearInterval(interval);
  };

  return (
    <ShopContext.Provider
      value={{
        shopId,
        isShopClosed,
        shopStartHour,
        shopEndHour,
        shopOpenHoursInterval,
        allowFutureDateOrders,
        noOfFutureDaysAllowed,
        minimumOrderAmount,
        minimumCollectionOrderAmount,
        defaultDeliveryCost,
        weeklyShopClosureDays,
        estimatedTimeForDelivery,
        estimatedTimeForCollection,
        notifyWhatsappNumber,
        adminWhatsappNumber,
        orderDeliveryRadius,
        shopPostcode,
        cashPaymentCollection,
        cashPaymentDelivery,
        shopTableCount,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
});

export default ShopContextProvider;

export const useShopContext = (): IShopContext => {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error(
      "useShopContext must be used within an ShopContextProvider"
    );
  }
  return context;
};
